import { CheckCircle2, XCircle, Clock, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import Badge from '../ui/Badge';

export default function ApprovalTimeline({ chainSnapshot, currentStep, status }) {
  if (!chainSnapshot || chainSnapshot.length === 0) {
    return (
      <p className="py-3 text-sm text-muted-foreground">
        No approval chain — this request was auto-approved.
      </p>
    );
  }

  return (
    <div className="relative space-y-0">
      {chainSnapshot.map((step, i) => {
        const isComplete = step.status === 'approved';
        const isRejected = step.status === 'rejected';
        const isCurrent = i === currentStep && status === 'pending';
        const isFuture = i > currentStep && status === 'pending';

        const Icon = isComplete ? CheckCircle2 : isRejected ? XCircle : isCurrent ? Clock : Circle;

        return (
          <div key={i} className="relative flex gap-4">
            {/* Line connector */}
            {i < chainSnapshot.length - 1 && (
              <div
                className={cn(
                  'absolute left-[13px] top-7 -bottom-1 w-0.5',
                  isComplete ? 'bg-emerald-400' : 'bg-border'
                )}
              />
            )}

            {/* Icon */}
            <div className="relative z-10 shrink-0">
              <Icon
                className={cn(
                  'h-7 w-7',
                  isComplete && 'text-emerald-500',
                  isRejected && 'text-red-500',
                  isCurrent && 'text-amber-500',
                  !isComplete && !isRejected && !isCurrent && 'text-muted-foreground/40'
                )}
              />
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              <div className="flex items-center gap-2">
                <span className={cn(
                  'text-sm font-medium',
                  isFuture ? 'text-muted-foreground' : 'text-foreground'
                )}>
                  {step.label || formatStepType(step)}
                </span>
                <Badge
                  variant={
                    isComplete ? 'success' :
                    isRejected ? 'danger' :
                    isCurrent ? 'warning' :
                    'default'
                  }
                >
                  {isComplete ? 'Approved' : isRejected ? 'Rejected' : isCurrent ? 'Awaiting' : 'Pending'}
                </Badge>
              </div>

              <p className="mt-0.5 text-xs text-muted-foreground">
                {step.type === 'manager' ? 'Direct Manager' : step.type === 'role' ? `Role: ${step.value}` : 'Specific Person'}
              </p>

              {step.note && (
                <div className="mt-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm italic text-foreground">
                  &ldquo;{step.note}&rdquo;
                </div>
              )}

              {step.decidedAt && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {new Date(step.decidedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatStepType(step) {
  if (step.type === 'manager') return 'Direct Manager';
  if (step.type === 'role') return `Role: ${step.value}`;
  return step.value || 'Approver';
}
